/**
 * Mission Ranker — Sprint 16 Package 4
 *
 * Ranks missions by Ikigai alignment. Missions that resonate across
 * multiple dimensions score higher; missions targeting the user's
 * weakest dimension get a "growth boost".
 *
 * Boosting rules:
 *  1 aligned dimension → 1.0x
 *  2 aligned dimensions → 1.5x
 *  3+ aligned dimensions → 2.0x
 *  Targets weakest dimension → +1.3x growth multiplier
 */
const DIMENSIONS = ['passion', 'mission', 'profession', 'vocation'];
/**
 * Rank a single mission against the user's Ikigai profile scores.
 */
export function rankMission(mission, scores) {
    const baseScore = mission.baseScore ?? 50;
    const alignedDimensions = getAlignedDimensions(mission, scores);
    const alignmentBoost = calculateAlignmentBoost(alignedDimensions.length);
    const weakest = getWeakestDimension(scores);
    const missionDims = mission.dimensions ?? [];
    const growthBoost = weakest && missionDims.includes(weakest) ? 1.3 : 1.0;
    const finalScore = baseScore * alignmentBoost * growthBoost;
    return {
        missionId: mission.id,
        baseScore,
        alignmentBoost,
        growthBoost,
        finalScore,
        alignedDimensions,
    };
}
/**
 * Rank and sort an array of missions by Ikigai alignment (descending).
 */
export function rankMissions(missions, scores) {
    return missions
        .map(m => ({ ...m, ranking: rankMission(m, scores) }))
        .sort((a, b) => b.ranking.finalScore - a.ranking.finalScore);
}
// ---------------------------------------------------------------------------
// Alignment detection
// ---------------------------------------------------------------------------
/**
 * Determine which Ikigai dimensions a mission aligns with by comparing
 * mission tags against dimension signals.
 */
export function getAlignedDimensions(mission, scores) {
    const aligned = [];
    const missionTags = new Set(mission.tags.map(t => t.toLowerCase()));
    for (const dimension of DIMENSIONS) {
        const dimScore = scores[dimension];
        if (!dimScore)
            continue;
        const overlap = calculateSignalOverlap(dimScore.signals, missionTags);
        if (overlap > 0.3) {
            aligned.push(dimension);
        }
    }
    return aligned;
}
/**
 * Calculate overlap between a dimension's signals and a mission's tags.
 * Returns a ratio (0-1) of matching signals.
 */
export function calculateSignalOverlap(signals, missionTags) {
    if (signals.length === 0 || missionTags.size === 0)
        return 0;
    const matches = signals.filter(signal => [...missionTags].some(tag => signal.toLowerCase().includes(tag) || tag.includes(signal.toLowerCase())));
    return matches.length / signals.length;
}
// ---------------------------------------------------------------------------
// Boost calculation
// ---------------------------------------------------------------------------
function calculateAlignmentBoost(alignedCount) {
    if (alignedCount >= 3)
        return 2.0;
    if (alignedCount === 2)
        return 1.5;
    return 1.0;
}
/**
 * Find the dimension with the lowest score.
 */
export function getWeakestDimension(scores) {
    let weakest = null;
    let lowestScore = Infinity;
    for (const dimension of DIMENSIONS) {
        const dimScore = scores[dimension];
        if (dimScore && dimScore.score < lowestScore) {
            lowestScore = dimScore.score;
            weakest = dimension;
        }
    }
    return weakest;
}
//# sourceMappingURL=mission-ranker.js.map